import { WebSocketService } from './WebSocketService'
import { env } from '../../config/env'
import { useAuthStore } from '../auth/store'
import { logger } from '../utils/logger'

/**
 * Глобальный WebSocket сервис - один экземпляр на всё приложение
 * Автоматически подключается при изменении авторизации
 */
class GlobalWebSocketManager {
  private service: WebSocketService
  private currentUserId: string | null = null
  private currentToken: string | null = null
  private isInitialized = false

  constructor() {
    this.service = WebSocketService.getInstance(env.resolveWsUrl)
    this.initAuthListener()
    this.initTokenExpirationHandler()
  }

  private initAuthListener() {
    if (this.isInitialized) return
    this.isInitialized = true

    logger.log('🚀 GlobalWebSocket: Инициализируем глобальный WebSocket менеджер')

    // Подписываемся на изменения авторизации ОДИН РАЗ
    const unsubscribe = useAuthStore.subscribe((state) => {
      const { webSocketUserId, token, status } = state
      console.log('🔥 ОТЛАДКА AuthStore: состояние изменилось', { webSocketUserId, status, hasToken: !!token })
      

      
      if (status === 'authenticated' && webSocketUserId && token) {
        // 🔥 ИСПРАВЛЕНИЕ: Проверяем и обновляем токен перед подключением
        const authStore = useAuthStore.getState()
        
        if (authStore.isTokenExpired()) {
          logger.warn('⚠️ GlobalWebSocket: Токен истек, пытаемся обновить')
          console.log('🔥 ОТЛАДКА: Токен истек, пытаемся обновить')
          
          // Пытаемся обновить токен
          authStore.refreshTokenIfNeeded().then(refreshed => {
            if (refreshed) {
              logger.log('✅ GlobalWebSocket: Токен обновлен, переподключаемся')
              // После обновления токена состояние изменится и сработает новый вызов этой функции
            } else {
              logger.warn('⚠️ GlobalWebSocket: Не удалось обновить истекший токен, принудительно очищаем сессию')
              // 🔥 КРИТИЧНО: Принудительно очищаем сессию если токен не обновился
              authStore.clear()
              this.currentUserId = null
              this.currentToken = null
              this.service.close()
            }
          }).catch(error => {
            logger.error('❌ GlobalWebSocket: Ошибка при обновлении истекшего токена:', error)
            // 🔥 КРИТИЧНО: При ошибке тоже очищаем сессию
            authStore.clear()
            this.currentUserId = null
            this.currentToken = null
            this.service.close()
          })
          return
        }
        
        // Если токен скоро истечет, обновляем его заранее
        if (authStore.isTokenExpiringSoon(5)) { // За 5 минут до истечения
          logger.log('🔄 GlobalWebSocket: Токен скоро истечет, обновляем заранее')
          authStore.refreshTokenIfNeeded().catch(error => {
            logger.warn('⚠️ GlobalWebSocket: Не удалось обновить токен заранее:', error)
          })
        }
        
        // Подключаемся только если изменились параметры
        if (this.currentUserId !== webSocketUserId || this.currentToken !== token) {
          console.log('🔥 ОТЛАДКА GlobalWebSocket: Подключаемся с userId:', webSocketUserId, 'token:', token?.substring(0, 10) + '...')
          logger.log('🔄 GlobalWebSocket: Параметры изменились, переподключаемся')
          this.currentUserId = webSocketUserId
          this.currentToken = token
          this.service.connect(webSocketUserId, token)
        }
      } else {
        // Отключаемся если нет авторизации
        if (this.currentUserId || this.currentToken) {
          logger.log('🔌 GlobalWebSocket: Нет авторизации, отключаемся')
          this.currentUserId = null
          this.currentToken = null
          this.service.close()
        }
      }
    })

    // Инициализируем с текущим состоянием
    const currentState = useAuthStore.getState()
    logger.log('🔄 GlobalWebSocket: Текущее состояние при инициализации', {
      status: currentState.status,
      hasUserId: !!currentState.webSocketUserId,
      hasToken: !!currentState.token
    })
    
    if (currentState.status === 'authenticated' && currentState.webSocketUserId && currentState.token) {
      // 🔥 ИСПРАВЛЕНИЕ: Проверяем и обновляем токен при инициализации
      if (currentState.isTokenExpired()) {
        logger.warn('⚠️ GlobalWebSocket: Токен истек при инициализации, пытаемся обновить')
        
        currentState.refreshTokenIfNeeded().then(refreshed => {
          if (refreshed) {
            logger.log('✅ GlobalWebSocket: Токен обновлен при инициализации')
            // После обновления токена состояние изменится и сработает подписчик
          } else {
            logger.warn('⚠️ GlobalWebSocket: Не удалось обновить токен при инициализации')
          }
        })
        return
      }
      
      this.currentUserId = currentState.webSocketUserId
      this.currentToken = currentState.token
      this.service.connect(currentState.webSocketUserId, currentState.token)
    }
  }

  private initTokenExpirationHandler() {
    // Обрабатываем события истечения токена от WebSocketService
    this.service.on('token_expired', async (data) => {
      logger.warn('⚠️ GlobalWebSocket: Получено событие истечения токена', data)
      
      try {
        const authStore = useAuthStore.getState()
        
        // Пытаемся обновить токен
        logger.log('🔄 GlobalWebSocket: Пытаемся обновить истекший токен')
        const refreshed = await authStore.refreshTokenIfNeeded()
        
        if (refreshed) {
          logger.log('✅ GlobalWebSocket: Токен успешно обновлен после истечения')
          
          // Получаем новый токен и переподключаемся
          const newAuthState = useAuthStore.getState()
          if (newAuthState.status === 'authenticated' && newAuthState.webSocketUserId && newAuthState.token) {
            this.currentUserId = newAuthState.webSocketUserId
            this.currentToken = newAuthState.token
            
            // Небольшая задержка перед переподключением
            setTimeout(() => {
              this.service.connect(newAuthState.webSocketUserId!, newAuthState.token!)
            }, 1000)
          }
        } else {
          logger.warn('⚠️ GlobalWebSocket: Не удалось обновить истекший токен, пользователь будет разлогинен')
          
          // Очищаем состояние авторизации
          authStore.clear()
          this.currentUserId = null
          this.currentToken = null
          this.service.close()
        }
      } catch (error) {
        logger.error('❌ GlobalWebSocket: Ошибка при обработке истечения токена:', error)
      }
    })

    // Обрабатываем предупреждения о скором истечении токена
    this.service.on('token_expiring_soon', async (data) => {
      logger.warn('⚠️ GlobalWebSocket: Токен скоро истечет', data)
      
      try {
        const authStore = useAuthStore.getState()
        
        // Заранее обновляем токен
        logger.log('🔄 GlobalWebSocket: Заранее обновляем токен')
        await authStore.refreshTokenIfNeeded()
      } catch (error) {
        logger.error('❌ GlobalWebSocket: Ошибка при заблаговременном обновлении токена:', error)
      }
    })
  }

  getService(): WebSocketService {
    return this.service
  }

  // Методы для компонентов
  on(event: string, handler: (data: any) => void) {
    this.service.on(event, handler)
  }

  off(event: string, handler: (data: any) => void) {
    this.service.off(event, handler)
  }

  send(data: any) {
    this.service.send(data)
  }
  
  // 🔥 НОВЫЙ МЕТОД: Принудительное переподключение с проверкой токена
  forceReconnect() {
    const currentState = useAuthStore.getState()
    
    if (currentState.status === 'authenticated' && currentState.webSocketUserId && currentState.token) {
      // Проверяем токен перед переподключением
      if (currentState.isTokenExpired()) {
        logger.warn('⚠️ GlobalWebSocket: Токен истек, не переподключаемся')
        return
      }
      
      logger.log('🔄 GlobalWebSocket: Принудительное переподключение')
      this.currentUserId = currentState.webSocketUserId
      this.currentToken = currentState.token
      this.service.connect(currentState.webSocketUserId, currentState.token)
    } else {
      logger.warn('⚠️ GlobalWebSocket: Нет данных для переподключения')
    }
  }
}

// Создаем единственный экземпляр
export const globalWebSocket = new GlobalWebSocketManager()
